/**
 * AI Assistant API.
 *
 * Per repo convention there is no shared axios instance — this file owns every
 * call the page makes, and the user is identified by an explicit `owner_id`.
 *
 * Streaming uses `fetch`, not axios: axios cannot expose a partial response
 * body in the browser. `EventSource` is no use either because it is GET-only
 * and the conversation history has to be POSTed.
 *
 * Note the slash-free `get_models` below. With a trailing slash FastAPI answers
 * 307 and builds `Location` from the proxied `Host`, which the browser refuses
 * to follow — model lists then render empty behind HTTPS while other calls
 * work. See the trailing-slash trap in `3_infra/nginx/README.md`.
 */
import axios from 'axios';
import { api_labelflow } from '../../../config.jsx';

const CHAT_ENDPOINT = `${api_labelflow}/ai_assistant/chat`;
const STREAM_ENDPOINT = `${api_labelflow}/ai_assistant/chat/stream`;
const STATUS_ENDPOINT = `${api_labelflow}/ai_assistant/status`;
const MODELS_ENDPOINT = `${api_labelflow}/model_management/get_models`;

/** Turn an axios/fetch failure into a message worth showing a user. */
const toFriendlyError = (error) => {
  const status = error?.response?.status;
  const detail =
    error?.response?.data?.detail || error?.response?.data?.message || error?.message;
  if (status === 400) {
    return new Error(detail || 'That question could not be sent. Please rephrase it.');
  }
  if (status === 503) {
    return new Error('The AI service is temporarily unavailable. Please try again shortly.');
  }
  return new Error(detail || 'Failed to reach the assistant. Please try again.');
};

/** Trim our message objects down to the {role, content} pairs the API wants. */
const toHistory = (messages = []) =>
  messages
    .filter((message) => !message.error && (message.content || '').trim())
    .map((message) => ({
      role: message.role === 'user' ? 'user' : 'assistant',
      content: message.content,
    }));

const buildPayload = ({ message, history, ownerId, includeContext, lang }) => ({
  message,
  history: toHistory(history),
  owner_id: ownerId || null,
  include_context: !!includeContext && !!ownerId,
  lang: lang || null,
});

/**
 * Non-streaming chat. Used as the fallback when streaming is unavailable
 * (an old backend, or a proxy that buffers the response anyway).
 */
export const sendAssistantMessage = async (options) => {
  try {
    const response = await axios.post(CHAT_ENDPOINT, buildPayload(options), {
      headers: { 'Content-Type': 'application/json' },
      timeout: 120000, // LLM inference, plus a grounded prompt to build
    });
    const data = response?.data || {};
    return {
      response: data.response || 'No response received.',
      engine: data.engine || 'gemini',
      contextUsed: !!data.context_used,
    };
  } catch (error) {
    console.error('Assistant API error:', error);
    throw toFriendlyError(error);
  }
};

/**
 * Streaming chat over server-sent events.
 *
 * Frames are `meta` (once), `delta` (many), then one terminal `done` or
 * `error`. Resolves with the assembled text; rejects on a transport failure so
 * the caller can fall back to `sendAssistantMessage`.
 *
 * `signal` comes from an AbortController — that is what the Stop button pulls.
 * An abort resolves with whatever text had arrived rather than rejecting,
 * because a deliberate stop is not an error and the partial answer is kept.
 */
export const streamAssistantMessage = async (
  options,
  { onMeta, onDelta, signal } = {},
) => {
  let response;
  try {
    response = await fetch(STREAM_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
      body: JSON.stringify(buildPayload(options)),
      signal,
    });
  } catch (error) {
    if (error?.name === 'AbortError') return { text: '', aborted: true };
    throw new Error('Failed to reach the assistant. Please check your connection.');
  }

  if (!response.ok) {
    // Read the body for FastAPI's `detail`, but never let that mask the status.
    let detail = '';
    try {
      const body = await response.json();
      detail = body?.detail || body?.message || '';
    } catch (error) {
      detail = '';
    }
    if (response.status === 400) {
      throw new Error(detail || 'That question could not be sent. Please rephrase it.');
    }
    if (response.status === 404) {
      // Backend predates the streaming route — the caller retries unstreamed.
      const notFound = new Error('Streaming is not available on this server.');
      notFound.code = 'NO_STREAM';
      throw notFound;
    }
    throw new Error(detail || `The assistant returned an error (${response.status}).`);
  }

  if (!response.body || typeof response.body.getReader !== 'function') {
    const unsupported = new Error('This browser cannot read a streamed response.');
    unsupported.code = 'NO_STREAM';
    throw unsupported;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let text = '';
  let engine = 'gemini';
  let contextUsed = false;
  let streamError = null;
  let aborted = false;

  /** Parse one `event:`/`data:` frame and apply it. */
  const handleFrame = (frame) => {
    let event = 'message';
    const dataLines = [];
    frame.split('\n').forEach((line) => {
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
      // A `:` comment line is a keep-alive; ignoring it is correct.
    });
    if (!dataLines.length) return;

    let payload;
    try {
      payload = JSON.parse(dataLines.join('\n'));
    } catch (error) {
      return; // a truncated frame is dropped rather than corrupting the text
    }

    if (event === 'meta') {
      engine = payload.engine || engine;
      contextUsed = !!payload.context_used;
      onMeta?.({ engine, contextUsed });
    } else if (event === 'delta' && payload.text) {
      text += payload.text;
      onDelta?.(payload.text, text);
    } else if (event === 'error') {
      streamError = payload.message || 'The response was cut short.';
    } else if (event === 'done') {
      engine = payload.engine || engine;
      contextUsed = !!payload.context_used;
    }
  };

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // Frames are separated by a blank line; the tail is an incomplete frame.
      const frames = buffer.split('\n\n');
      buffer = frames.pop() || '';
      frames.forEach((frame) => frame.trim() && handleFrame(frame));
    }
    if (buffer.trim()) handleFrame(buffer);
  } catch (error) {
    if (error?.name === 'AbortError') {
      aborted = true;
    } else {
      // Text already rendered is kept; the caller surfaces the reason.
      streamError = error?.message || 'The response was cut short.';
    }
  } finally {
    try {
      reader.releaseLock();
    } catch (error) {
      // Already released by an abort.
    }
  }

  return { text, engine, contextUsed, error: streamError, aborted };
};

/** Backend capability report: is Gemini configured, is there workspace data. */
export const fetchAssistantStatus = async (ownerId) => {
  try {
    const response = await axios.get(STATUS_ENDPOINT, {
      params: ownerId ? { owner_id: ownerId } : {},
      timeout: 15000,
    });
    const data = response?.data || {};
    return {
      geminiConfigured: !!data.gemini_configured,
      engine: data.engine || (data.gemini_configured ? 'gemini' : 'rulebased'),
      streaming: data.streaming !== false,
      workspaceContextAvailable: !!data.workspace_context_available,
    };
  } catch (error) {
    // A missing /status is not worth a visible error; assume the happy path.
    console.warn('Assistant status unavailable', error);
    return null;
  }
};

/**
 * The user's projects, models and versions — one call, already nested.
 *
 * The page uses this for the workspace panel and the contextual starter
 * prompts. The backend derives its own grounding snapshot from `owner_id`
 * independently, so nothing here is trusted as input to the model.
 */
export const fetchWorkspace = async (ownerId) => {
  if (!ownerId) return { projects: [], totals: { projects: 0, models: 0, versions: 0 } };
  try {
    const response = await axios.get(MODELS_ENDPOINT, {
      params: { owner_id: ownerId },
      timeout: 30000,
    });
    const projects = Array.isArray(response?.data) ? response.data : [];
    let models = 0;
    let versions = 0;
    let unlabeled = 0;
    let trained = 0;
    projects.forEach((project) => {
      (project.models || []).forEach((model) => {
        models += 1;
        (model.versions || []).forEach((version) => {
          versions += 1;
          unlabeled += version?.labelingStatus?.unlabeledImages || 0;
          if ((version?.trainingStatus || '').toLowerCase() === 'completed') trained += 1;
        });
      });
    });
    return {
      projects,
      totals: { projects: projects.length, models, versions, unlabeled, trained },
    };
  } catch (error) {
    console.warn('Assistant: could not load workspace', error);
    throw toFriendlyError(error);
  }
};
